import {
    CylinderGeometry,
    PlaneGeometry,
    MeshStandardMaterial,
    Mesh,
    Group,
    CanvasTexture,
    DoubleSide,
} from 'three'
import Experience from '../Experience.js'

// Fanion + mât sur chaque green : repère d'échelle et de jeu attendu par
// tout golfeur, absent jusqu'ici (seul un label flottant marquait le trou).
export default class Flags {
    constructor(terrain) {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.time = this.experience.time
        this.terrain = terrain

        this.items = []

        this.poleHeight = 0.014
        this.poleGeometry = new CylinderGeometry(0.0004, 0.0004, this.poleHeight, 6)
        this.poleGeometry.translate(0, this.poleHeight / 2, 0)

        this.flagWidth = 0.007
        this.flagGeometry = new PlaneGeometry(this.flagWidth, 0.0048, 10, 3)
        this.flagGeometry.translate(0.0035, -0.0012, 0)

        this.flagUniforms = { uTime: { value: 0 } }

        this.poleMaterial = new MeshStandardMaterial({
            color: '#f4f1e8',
            roughness: 0.35,
            metalness: 0.2
        })

        this.createFlags()
    }

    createFlagTexture(hole) {
        const size = 96
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')

        ctx.fillStyle = '#b55233'
        ctx.fillRect(0, 0, size, size)

        ctx.fillStyle = 'rgba(255, 249, 238, 0.95)'
        ctx.font = '700 50px Satoshi, sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(String(hole), size / 2, size / 2 + 3)

        const texture = new CanvasTexture(canvas)
        texture.needsUpdate = true
        return texture
    }

    createFlags() {
        const holes = this.resources.items.holes

        for (const feature of holes.features) {
            const hole = feature.properties.hole
            const [geoX, geoY] = feature.geometry.coordinates
            const x = -geoX
            const z = -geoY
            const y = this.terrain.getHeightAt(x, z)

            const group = new Group()
            group.position.set(x, y, z)

            const pole = new Mesh(this.poleGeometry, this.poleMaterial)
            group.add(pole)

            const phase = Math.random() * Math.PI * 2
            const flagMaterial = new MeshStandardMaterial({
                map: this.createFlagTexture(hole),
                roughness: 0.8,
                metalness: 0,
                side: DoubleSide
            })
            this.addClothWave(flagMaterial, phase)

            const flag = new Mesh(this.flagGeometry, flagMaterial)
            flag.position.y = this.poleHeight * 0.88
            group.add(flag)

            this.scene.add(group)
            this.items.push({ group, flag, phase })
        }
    }

    // Ondulation de tissu : amplitude nulle côté mât, croissante vers le
    // bord libre, normale recalculée pour que la lumière glisse sur les plis.
    addClothWave(material, phase) {
        const width = this.flagWidth.toFixed(6)

        material.onBeforeCompile = (shader) => {
            shader.uniforms.uTime = this.flagUniforms.uTime
            shader.uniforms.uPhase = { value: phase }

            shader.vertexShader = shader.vertexShader
                .replace(
                    '#include <common>',
                    `#include <common>
                    uniform float uTime;
                    uniform float uPhase;
                    float flagWave(float x) {
                        float k = x / ${width};
                        return (sin(k * 7.0 - uTime * 4.2 + uPhase) * 0.7 + sin(k * 13.0 - uTime * 6.1 + uPhase * 1.3) * 0.3) * k * ${width} * 0.09;
                    }`
                )
                .replace(
                    '#include <beginnormal_vertex>',
                    `#include <beginnormal_vertex>
                    float flagSlope = (flagWave(position.x + 0.0001) - flagWave(position.x - 0.0001)) / 0.0002;
                    objectNormal = normalize(vec3(-flagSlope, 0.0, 1.0));`
                )
                .replace(
                    '#include <begin_vertex>',
                    `#include <begin_vertex>
                    transformed.z += flagWave(position.x);`
                )
        }
    }

    update() {
        const t = this.time.elapsed * 0.001
        this.flagUniforms.uTime.value = t

        for (const item of this.items) {
            item.flag.rotation.y = Math.sin(t * 0.6 + item.phase) * 0.12
        }
    }

    destroy() {
        for (const item of this.items) {
            this.scene.remove(item.group)
            item.flag.material?.map?.dispose?.()
            item.flag.material?.dispose?.()
        }

        this.poleGeometry?.dispose?.()
        this.flagGeometry?.dispose?.()
        this.poleMaterial?.dispose?.()

        this.items = []
    }
}
